import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';

const gcd = (a, b) => b === 0 ? Math.abs(a) : gcd(b, a % b);

const simplify = (num, den) => {
  if (den === 0) return { num: NaN, den: 1 };
  const g = gcd(num, den);
  let n = num / g, d = den / g;
  if (d < 0) { n = -n; d = -d; }
  return { num: n, den: d };
};

const toMixed = (num, den) => {
  if (den === 0) return 'undefined';
  if (num === 0) return '0';
  const sign = (num < 0) !== (den < 0) ? '-' : '';
  const n = Math.abs(num), d = Math.abs(den);
  const whole = Math.floor(n / d);
  const rem = n % d;
  if (whole === 0) return `${sign}${n}/${d}`;
  if (rem === 0) return `${sign}${whole}`;
  return `${sign}${whole} ${rem}/${d}`;
};

const FractionCalculator = () => {
  const [a, setA] = useState({ num: 1, den: 2 });
  const [b, setB] = useState({ num: 1, den: 3 });
  const [op, setOp] = useState('+');

  const result = useMemo(() => {
    let n, d;
    switch (op) {
      case '+': n = a.num * b.den + b.num * a.den; d = a.den * b.den; break;
      case '-': n = a.num * b.den - b.num * a.den; d = a.den * b.den; break;
      case '×': n = a.num * b.num; d = a.den * b.den; break;
      case '÷': n = a.num * b.den; d = a.den * b.num; break;
      default: n = 0; d = 1;
    }
    return simplify(n, d);
  }, [a, b, op]);

  const decimal = isFinite(result.num / result.den) ? (result.num / result.den).toFixed(6).replace(/\.?0+$/, '') : '—';

  return (
    <CalculatorLayout
      title="Fraction Calculator"
      subtitle="Add, subtract, multiply, and divide fractions — with simplification and decimal form."
      category="Math"
      description={<p>Work with fractions the way you would on paper. This tool adds, subtracts, multiplies, and divides two fractions, then presents the answer in both simplified and mixed-number form, along with its decimal equivalent.</p>}
      formulaNote="a/b + c/d = (ad + bc) / bd   •  a/b ÷ c/d = ad / bc"
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
          <FractionInput fraction={a} onChange={setA} />

          <div className="flex gap-1">
            {['+', '-', '×', '÷'].map(o => (
              <Button key={o} onClick={() => setOp(o)} variant={op === o ? 'default' : 'outline'}
                className={`h-12 w-12 font-display text-xl font-bold ${op === o ? 'bg-saffron-500 hover:bg-saffron-600 text-white border-saffron-500' : 'border-slate-300'}`}>
                {o}
              </Button>
            ))}
          </div>

          <FractionInput fraction={b} onChange={setB} />

          <div className="font-display text-3xl text-slate-400">=</div>

          <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-4 min-w-[140px]">
            <div className="flex flex-col items-center display-font">
              <div className="text-3xl font-bold text-bharat-navy">{result.num}</div>
              <div className="w-full h-0.5 bg-bharat-navy my-1" />
              <div className="text-3xl font-bold text-bharat-navy">{result.den}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-8">
          <ResBox label="Simplified" value={`${result.num} / ${result.den}`} />
          <ResBox label="Mixed Number" value={toMixed(result.num, result.den)} tone="saffron" />
          <ResBox label="Decimal" value={decimal} tone="green" />
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { a: { num: 1, den: 2 }, b: { num: 1, den: 4 }, op: '+', label: '1/2 + 1/4' },
            { a: { num: 3, den: 4 }, b: { num: 1, den: 2 }, op: '-', label: '3/4 − 1/2' },
            { a: { num: 2, den: 3 }, b: { num: 3, den: 5 }, op: '×', label: '2/3 × 3/5' },
            { a: { num: 5, den: 8 }, b: { num: 1, den: 4 }, op: '÷', label: '5/8 ÷ 1/4' },
          ].map((p) => (
            <Button key={p.label} variant="outline" onClick={() => { setA(p.a); setB(p.b); setOp(p.op); }} className="text-xs border-slate-300">
              {p.label}
            </Button>
          ))}
        </div>
      </div>
    </CalculatorLayout>
  );
};

const FractionInput = ({ fraction, onChange }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-3 min-w-[120px]">
    <div className="flex flex-col items-center gap-1">
      <Input type="number" value={fraction.num} onChange={(e) => onChange({ ...fraction, num: +e.target.value || 0 })} className="w-20 text-center display-font font-bold text-xl h-10" />
      <div className="w-20 h-0.5 bg-bharat-navy" />
      <Input type="number" value={fraction.den} onChange={(e) => onChange({ ...fraction, den: +e.target.value || 1 })} className="w-20 text-center display-font font-bold text-xl h-10" />
    </div>
  </div>
);

const ResBox = ({ label, value, tone }) => {
  const cls = tone === 'saffron' ? 'bg-saffron-50 border-saffron-200 text-saffron-700' : tone === 'green' ? 'bg-indgreen-50 border-indgreen-200 text-indgreen-700' : 'bg-white border-slate-200 text-bharat-navy';
  return (
    <div className={`p-4 rounded-lg border ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="display-font font-bold text-xl mt-1">{value}</div>
    </div>
  );
};

export default FractionCalculator;
